import type {
  AppearanceSettings,
  ChartData,
  ChartSettings,
  Dashboard,
  ExecutionRow,
  ImportBatch,
  ImportPreview,
  Instrument,
  KiteStatus,
  MonthCalendar,
  PositionsResponse,
  PositionsTableSettings,
  Rule,
  StrategyOption,
  TradeCardSettings,
  Trade,
  Tradebook,
} from "./types";

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    // Session lives in a cookie; without this the browser drops it on same-origin
    // fetches that specify other options.
    credentials: "same-origin",
    ...init,
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      // FastAPI validation errors arrive as a list of objects, not a string.
      detail = Array.isArray(body.detail)
        ? body.detail.map((d: { msg: string }) => d.msg).join("; ")
        : (body.detail ?? detail);
    } catch {
      /* response had no JSON body — keep the status text */
    }
    throw new ApiError(detail, response.status);
  }

  return response.status === 204 ? (undefined as T) : response.json();
}

function json<T>(path: string, method: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

function scopeParam(scope: number | "all"): Record<string, string> {
  return scope === "all" ? { all_books: "true" } : { tradebook_id: String(scope) };
}

export const api = {
  // --- Session ---
  health: () => request<{ status: string; ai_enabled: boolean }>("/api/health"),
  session: () => request<{ authenticated: boolean }>("/api/session"),
  login: (password: string) => json<{ ok: boolean }>("/api/login", "POST", { password }),
  logout: () => json<{ ok: boolean }>("/api/logout", "POST", {}),

  // --- Tradebooks ---
  tradebooks: () => request<Tradebook[]>("/api/tradebooks"),
  createTradebook: (body: Partial<Tradebook>) =>
    json<Tradebook>("/api/tradebooks", "POST", body),
  updateTradebook: (id: number, body: Partial<Tradebook>) =>
    json<Tradebook>(`/api/tradebooks/${id}`, "PATCH", body),
  deleteTradebook: (id: number) =>
    request<{ name: string; executions: number; trades: number; journals: number }>(
      `/api/tradebooks/${id}`,
      { method: "DELETE" },
    ),

  // --- Zerodha Kite ---
  kiteStatus: () => request<KiteStatus[]>("/api/zerodha/status"),
  kiteSync: (tradebookId: number) =>
    json<{ note: string; inserted: number; skipped: number; ok: boolean }>(
      `/api/zerodha/sync/${tradebookId}`,
      "POST",
      {},
    ),

  // --- Trades ---
  trades: (params: Record<string, string | number | boolean | undefined>) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") query.set(key, String(value));
    }
    return request<Trade[]>(`/api/trades?${query}`);
  },
  trade: (id: number) => request<Trade>(`/api/trades/${id}`),
  createTrade: (body: unknown) => json<Trade>("/api/trades", "POST", body),
  deleteTrade: (id: number) => request<void>(`/api/trades/${id}`, { method: "DELETE" }),
  editTrade: (id: number, body: unknown) => json<Trade>(`/api/trades/${id}`, "PUT", body),
  addLeg: (id: number, body: unknown) => json<Trade>(`/api/trades/${id}/legs`, "POST", body),
  deleteLeg: (tradeId: number, legId: number) =>
    request<Trade>(`/api/trades/${tradeId}/legs/${legId}`, { method: "DELETE" }),
  saveLotJournal: (tradeId: number, entryTradeId: string, body: unknown) =>
    json<Trade>(
      `/api/trades/${tradeId}/lots/${encodeURIComponent(entryTradeId)}`,
      "PUT",
      body,
    ),
  saveJournal: (id: number, body: unknown) =>
    json<Trade>(`/api/trades/${id}/journal`, "PUT", body),
  rebuild: (tradebookId: number) =>
    json<{ closed: number; open: number; anomalies: number }>(
      `/api/trades/rebuild/${tradebookId}`,
      "POST",
      {},
    ),

  // --- Open positions ---
  positions: (scope: number | "all", refresh = false) =>
    request<PositionsResponse>(
      `/api/positions?${new URLSearchParams({
        ...scopeParam(scope),
        ...(refresh ? { refresh: "true" } : {}),
      })}`,
    ),
  positionChart: (tradeId: number, months?: number) =>
    request<ChartData>(
      `/api/positions/${tradeId}/chart${months ? `?months=${months}` : ""}`,
    ),

  // --- Chart settings ---
  chartSettings: () => request<ChartSettings>("/api/settings/chart"),
  appearance: () => request<AppearanceSettings>("/api/settings/appearance"),
  positionsTable: () => request<PositionsTableSettings>("/api/settings/positions-table"),
  tradeCard: () => request<TradeCardSettings>("/api/settings/trade-card"),
  saveTradeCard: (body: TradeCardSettings) =>
    json<TradeCardSettings>("/api/settings/trade-card", "PUT", body),
  savePositionsTable: (body: PositionsTableSettings) =>
    json<PositionsTableSettings>("/api/settings/positions-table", "PUT", body),
  saveAppearance: (body: AppearanceSettings) =>
    json<AppearanceSettings>("/api/settings/appearance", "PUT", body),
  saveChartSettings: (body: ChartSettings) =>
    json<ChartSettings>("/api/settings/chart", "PUT", body),

  // --- Rules ---
  rules: () => request<Rule[]>("/api/rules"),
  createRule: (body: unknown) => json<Rule>("/api/rules", "POST", body),
  updateRule: (id: number, body: unknown) => json<Rule>(`/api/rules/${id}`, "PATCH", body),
  seedRules: () => json<{ created: number }>("/api/rules/seed", "POST", {}),

  // --- Instruments ---
  searchInstruments: (q: string) =>
    request<Instrument[]>(`/api/instruments/search?q=${encodeURIComponent(q)}`),
  instrumentCount: () =>
    request<{ total: number; with_names: number; with_sector: number }>(
      "/api/instruments/count",
    ),
  uploadInstruments: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<{
      added: number;
      updated: number;
      skipped_non_equity: number;
      has_company_names: boolean;
      note?: string;
    }>("/api/instruments/upload", { method: "POST", body: form });
  },

  // --- Strategies ---
  strategies: () => request<StrategyOption[]>("/api/strategies"),
  createStrategy: (body: unknown) => json<StrategyOption>("/api/strategies", "POST", body),
  updateStrategy: (id: number, body: unknown) =>
    json<StrategyOption>(`/api/strategies/${id}`, "PATCH", body),

  // --- Screenshots ---
  uploadScreenshot: (tradeId: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<{ screenshot_path: string }>(`/api/trades/${tradeId}/screenshot`, {
      method: "POST",
      body: form,
    });
  },
  deleteScreenshot: (tradeId: number) =>
    request<void>(`/api/trades/${tradeId}/screenshot`, { method: "DELETE" }),

  // --- Dashboard ---
  // `scope` is either one book's id or "all"; the server refuses an omitted
  // one rather than quietly combining books.
  dashboard: (scope: number | "all", params: Record<string, string> = {}) => {
    const query = new URLSearchParams({ ...scopeParam(scope), ...params });
    return request<Dashboard>(`/api/dashboard?${query}`);
  },
  monthCalendar: (scope: number | "all", year: number, month: number) => {
    const query = new URLSearchParams({
      ...scopeParam(scope),
      year: String(year),
      month: String(month),
    });
    return request<MonthCalendar>(`/api/dashboard/calendar?${query}`);
  },

  // --- Import ---
  importFile: (
    tradebookId: number,
    file: File,
    commit: boolean,
    adoptClientId = false,
  ) => {
    const form = new FormData();
    form.append("tradebook_id", String(tradebookId));
    form.append("file", file);
    if (adoptClientId) form.append("adopt_client_id", "true");
    return request<ImportPreview>(`/api/imports/${commit ? "commit" : "preview"}`, {
      method: "POST",
      body: form,
    });
  },

  // --- Import batches: undo or move a wrong import ---
  batches: (tradebookId?: number) =>
    request<ImportBatch[]>(
      `/api/imports/batches${tradebookId ? `?tradebook_id=${tradebookId}` : ""}`,
    ),
  moveBatch: (batchId: number, tradebookId: number) =>
    json<ImportBatch>(`/api/imports/batches/${batchId}/move`, "POST", {
      tradebook_id: tradebookId,
    }),
  deleteBatch: (batchId: number) =>
    request<{ deleted_executions: number }>(`/api/imports/batches/${batchId}`, {
      method: "DELETE",
    }),

  // --- Raw broker fills ---
  executions: (tradebookId: number, symbol?: string) =>
    request<ExecutionRow[]>(
      `/api/executions?tradebook_id=${tradebookId}${symbol ? `&symbol=${encodeURIComponent(symbol)}` : ""}`,
    ),
  editExecution: (id: number, body: unknown) =>
    json<ExecutionRow>(`/api/executions/${id}`, "PATCH", body),
  revertExecution: (id: number) =>
    json<ExecutionRow>(`/api/executions/${id}/revert`, "POST", {}),
  deleteExecution: (id: number) =>
    request<{ deleted: number }>(`/api/executions/${id}`, { method: "DELETE" }),
};
